import { App, Form } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CustomActionButtons,
  CustomModal,
  Loader,
} from "sipli-fleet-core/components";
import {
  useMutationCreateCustomer,
  useMutationUpdateCustomer,
  useQueryGetCustomerById,
} from "src/queries/customers.queries";
import {
  useQueryGetClientCompetitorLookup,
  useQueryGetClientPresetLookup,
  useQueryGetClientPositionTypeLookup,
  useQueryGetClientSourceLookup,
  useQueryGetClientStatusLookup,
  useQueryGetClientTypeLookup,
  useQueryGetCountryLookup,
  useQueryGetReferenceCountries,
  useQueryGetCurrencyLookup,
  useQueryGetDistanceLookup,
  useQueryGetFuelLookup,
  useQueryGetLanguageLookup,
  useQueryGetPaymentLookup,
  useQueryGetPaymentSubsUnitLookup,
  useQueryGetPaymentTypeLookup,
  useQueryGetPositionLookup,
  useQueryGetPprPaymentTermLookup,
  useQueryGetProductLookup,
  useQueryGetTemperatureLookup,
  useQueryGetVolumeLookup,
  useQueryGetWeightLookup,
} from "src/queries/lookup.queries";
import type { Customer } from "src/services/customerService";
import type { LookupItem, LookupResponse } from "src/services/lookup/lookupService";
import { useSelectDistributor } from "src/store/selectDistributorStore";
import CustomerTabs, {
  type CustomerSelectOption,
} from "./CustomerTabs";

import { buildCustomerUpdatePayload } from "./customerUpdatePayload";

interface CreateCustomerProps {
  open: boolean;
  editRecord?: Customer | null;
  onClose: () => void;
  refetchList: () => Promise<void>;
}

type CustomerDetailPayload = Record<string, unknown>;

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : {};

const getResponseRecord = (data: unknown) => {
  const response = asRecord(data);

  if ("client" in response || "invoiceSetting" in response) return response;
  return asRecord(response.client ?? response);
};

const getNested = (record: Record<string, unknown>, key: string) =>
  asRecord(record[key]);

const getLookupItems = (data: unknown): LookupItem[] => {
  const responseData = asRecord(data);
  const payload = asRecord(responseData.data ?? responseData);
  const lookupResponse = payload as LookupResponse;
  const items =
    lookupResponse.response ??
    lookupResponse.data ??
    (Array.isArray(payload) ? (payload as LookupItem[]) : []);

  return Array.isArray(items) ? items : [];
};

const mapLookupOptions = (data: unknown): CustomerSelectOption[] =>
  getLookupItems(data)
    .map((item) => {
      const label = item.name ?? item.title ?? item.label ?? item.code ?? "-";

      return {
        label: String(label),
        value: item.id ?? item.value ?? item.code ?? "",
      };
    })
    .filter((option) => option.value !== "");

const mapCountryCodeOptions = (data: unknown): CustomerSelectOption[] =>
  getLookupItems(data)
    .filter((item) => item.code && item.name)
    .map((item) => ({ label: item.name!, value: item.code! }));

const translateClientStatusOptions = (
  options: CustomerSelectOption[],
  translate: (key: string) => string,
) =>
  options.map((option) => {
    const normalizedLabel = option.label.toLowerCase().replace(/[^a-z]/g, "");
    const translationKey =
      normalizedLabel === "active"
        ? "active"
        : normalizedLabel === "inactive"
          ? "inactive"
          : normalizedLabel === "blocked"
            ? "blocked"
            : undefined;

    return translationKey
      ? { ...option, label: translate(translationKey) }
      : option;
  });

const getStringValue = (value: unknown, fallback = "") =>
  value === undefined || value === null ? fallback : String(value);

const getOptionalNumberValue = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const getOptionalIdValue = (...values: unknown[]) => {
  for (const value of values) {
    const record = asRecord(value);
    const id = getOptionalNumberValue(record.id ?? value);

    if (id !== undefined) return id;
  }

  return undefined;
};

const getBusinessTypeValue = (client: Record<string, unknown>) => {
  const businessTypes = Array.isArray(client.businessTypes)
    ? client.businessTypes
    : Array.isArray(client.businessTypeIds)
      ? client.businessTypeIds
      : [];

  if (businessTypes.length > 0) {
    return businessTypes
      .map((businessType) => {
        const record = asRecord(businessType);
        return record.name ?? record.id ?? businessType;
      })
      .filter(Boolean)
      .join(", ");
  }

  return client.businessType ?? client.businessTypeId;
};

const getBooleanSelectValue = (value: unknown) => {
  if (typeof value === "boolean") return String(value);
  if (typeof value === "number") return String(Boolean(value));
  if (typeof value === "string") {
    const normalizedValue = value.toLowerCase();

    if (normalizedValue === "true" || normalizedValue === "false") {
      return normalizedValue;
    }
  }

  return undefined;
};

const isEmptyValue = (value: unknown) =>
  value === undefined ||
  value === null ||
  value === "" ||
  value === "input" ||
  (Array.isArray(value) && value.length === 0);

const isEmptyObject = (value: unknown) =>
  !!value &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  Object.keys(value).length === 0;

const pruneCreatePayload = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    const nextArray = value
      .map((item) => pruneCreatePayload(item))
      .filter((item) => {
        if (isEmptyValue(item)) return false;
        if (typeof item === "number" && item === 0) return false;
        if (item && typeof item === "object" && !Array.isArray(item)) {
          return Object.keys(item).length > 0;
        }
        return true;
      });

    return nextArray.length > 0 ? nextArray : undefined;
  }

  if (!value || typeof value !== "object") {
    return isEmptyValue(value) ? undefined : value;
  }

  return Object.entries(value as Record<string, unknown>).reduce<
    Record<string, unknown>
  >((nextValue, [key, item]) => {
    const prunedItem = pruneCreatePayload(item);

    if (prunedItem === undefined) return nextValue;
    if (typeof prunedItem === "number" && prunedItem === 0) return nextValue;
    if (isEmptyObject(prunedItem)) return nextValue;

    nextValue[key] = prunedItem;
    return nextValue;
  }, {});
};

const getObjectId = (
  record: Record<string, unknown>,
  objectKey: string,
  fallbackKey: string,
) => {
  const nested = getNested(record, objectKey);
  return getOptionalNumberValue(nested.id ?? record[fallbackKey]);
};

const getObjectName = (
  record: Record<string, unknown>,
  objectKey: string,
  fallbackKey: string,
) => {
  const nested = getNested(record, objectKey);
  return getStringValue(nested.name ?? record[fallbackKey]);
};

const pickDetailData = (detail?: CustomerDetailPayload | null) => {
  const response = getResponseRecord(detail);
  const client = asRecord(response.client ?? response);
  const additionalInformation = asRecord(response.additionalInformation);
  const invoiceSetting = asRecord(response.invoiceSetting);
  const userInterfaceDetails = asRecord(response.userInterfaceDetails);
  const contact = asRecord(response.contact);
  const productPricing = Array.isArray(response.productPricing)
    ? (response.productPricing as Array<Record<string, unknown>>)
    : [];
  const productPricingRecord = Array.isArray(response.productPricing)
    ? productPricing[0] ?? {}
    : asRecord(response.productPricing);

  return {
    response,
    client,
    additionalInformation,
    invoiceSetting,
    userInterfaceDetails,
    contact,
    firstProductPricing: productPricingRecord,
  };
};

const mapCustomerToFormValues = (detail?: CustomerDetailPayload | null) => {
  const {
    client,
    contact,
    invoiceSetting,
    userInterfaceDetails,
    firstProductPricing,
  } = pickDetailData(detail);

  return {
    companyName: client.companyName,
    clientType: getOptionalNumberValue(
      client.clientTypeId ?? getObjectId(client, "clientType", "clientTypeId"),
    ),
    state:
      getOptionalNumberValue(
        client.statusId ?? getObjectId(client, "status", "statusId"),
      ) ?? getObjectName(client, "status", "state"),
    email: client.email ?? client.mail,
    clientManager: getOptionalNumberValue(
      client.clientManagerId ??
        getObjectId(client, "clientManager", "clientManagerId"),
    ),
    clientPreset: getOptionalIdValue(
      client.preset,
      client.clientPreset,
      client.clientPresetId,
    ),
    comment: client.comment,
    language: getOptionalNumberValue(
      client.languageId ?? getObjectId(client, "language", "languageId"),
    ),
    country: client.countryCode ?? (typeof client.country === "string" ? client.country : undefined),

    // Contact
    name: contact.name ?? client.name,
    surname: contact.surname ?? client.surname,
    phoneNumber: contact.phoneNumber ?? client.phoneNumber,
    contactEmail: contact.email ?? client.email ?? client.mail,
    positionType: getOptionalNumberValue(
      contact.positionId ?? getObjectId(contact, "position", "positionId"),
    ),
    positionName: contact.positionName,

    // Additional Information
    legalName: client.legalName,
    businessType: getBusinessTypeValue(client),
    regNo: client.regNo,
    address: client.address,
    billAddress: client.billAddress,
    handlingInPlace: client.handlingInPlace,
    vatNr: client.vat,
    bank: client.bank,
    accountNo: client.accountNo,
    swift: client.swift,
    source: getOptionalIdValue(
      client.source,
      client.sourceId,
      getObjectId(client, "source", "sourceId"),
    ),
    competitor: getOptionalIdValue(
      client.competitor,
      client.competitorsId,
      getObjectId(client, "competitor", "competitorsId"),
    ),

    // Invoice Setting
    invoiceVat: getBooleanSelectValue(invoiceSetting.userVat),
    invoiceCountry: getOptionalIdValue(
      invoiceSetting.country,
      invoiceSetting.countryId,
      getObjectId(invoiceSetting, "country", "countryId"),
    ),
    paymentType: getOptionalIdValue(
      invoiceSetting.paymentType,
      invoiceSetting.paymentTypeId,
      getObjectId(invoiceSetting, "paymentType", "paymentTypeId"),
    ),
    paymentPeriod: getOptionalIdValue(
      invoiceSetting.paymentPeriod,
      invoiceSetting.paymentPeriodId,
      getObjectId(invoiceSetting, "paymentPeriod", "paymentPeriodId"),
    ),
    paySubscriptionUntil: getOptionalIdValue(
      invoiceSetting.paySubscription,
      invoiceSetting.paySubscriptionId,
      getObjectId(invoiceSetting, "paySubscription", "paySubscriptionId"),
    ),
    pprPaymentTerm: getOptionalIdValue(
      invoiceSetting.ppr,
      invoiceSetting.pprId,
      getObjectId(invoiceSetting, "ppr", "pprId"),
    ),
    invoiceVatNr: invoiceSetting.vat ?? client.vat,
    invoiceBank: invoiceSetting.bank ?? client.bank,

    // Product Pricing
    product: getOptionalIdValue(
      firstProductPricing.product,
      firstProductPricing.productId,
      firstProductPricing.modelId,
    ),
    price: firstProductPricing.price ?? firstProductPricing.prices,

    // Interface Details
    currencyId: getOptionalIdValue(
      userInterfaceDetails.currency,
      userInterfaceDetails.currencyId,
    ),
    distanceId: getOptionalIdValue(
      userInterfaceDetails.distance,
      userInterfaceDetails.distanceId,
    ),
    volumeId: getOptionalIdValue(
      userInterfaceDetails.volume,
      userInterfaceDetails.volumeId,
    ),
    temperatureId: getOptionalIdValue(
      userInterfaceDetails.temperature,
      userInterfaceDetails.temperatureId,
    ),
    weightId: getOptionalIdValue(
      userInterfaceDetails.weight,
      userInterfaceDetails.weightId,
    ),
    fuelConsumptionId: getOptionalIdValue(
      userInterfaceDetails.fuel,
      userInterfaceDetails.fuelConsumption,
      userInterfaceDetails.fuelConsumptionId ??
        userInterfaceDetails.measurementOfFuelConsumptionId,
    ),
    serviceId: getOptionalNumberValue(firstProductPricing.serviceId),
  };
};

const CreateCustomer = ({
  open,
  editRecord,
  onClose,
  refetchList,
}: CreateCustomerProps) => {
  const [activeKey, setActiveKey] = useState("main_information");
  const [form] = Form.useForm();
  const initialValues = useRef<Record<string, unknown>>({});
  const { t, i18n } = useTranslation();
  const { selectedDistributor } = useSelectDistributor();
  const { message } = App.useApp()
  const editCustomerId = editRecord?.id;
  const { data: customerDetailData, isLoading: isCustomerDetailLoading } =
    useQueryGetCustomerById(editCustomerId, {
      enabled: open && !!editCustomerId,
    });
  const { mutateAsync: createCustomerMutation, isPending: isCreating } =
    useMutationCreateCustomer();
  const { mutateAsync: updateCustomer, isPending: isUpdating } =
    useMutationUpdateCustomer();
  const { data: clientTypeLookupData, isLoading: isClientTypeLookupLoading } =
    useQueryGetClientTypeLookup({}, { enabled: open });
  const { data: clientStatusLookupData, isLoading: isClientStatusLookupLoading } =
    useQueryGetClientStatusLookup({}, { enabled: open });
  const { data: clientPresetLookupData, isLoading: isClientPresetLookupLoading } =
    useQueryGetClientPresetLookup({}, { enabled: open });
  const { data: languageLookupData, isLoading: isLanguageLookupLoading } =
    useQueryGetLanguageLookup({}, { enabled: open });
  const { data: countryLookupData, isLoading: isCountryLookupLoading } =
    useQueryGetCountryLookup({}, { enabled: open });
  const { data: referenceCountriesData, isLoading: isReferenceCountriesLoading } =
    useQueryGetReferenceCountries({}, { enabled: open });
  const { data: currencyLookupData, isLoading: isCurrencyLookupLoading } =
    useQueryGetCurrencyLookup({}, { enabled: open });
  const { data: distanceLookupData, isLoading: isDistanceLookupLoading } =
    useQueryGetDistanceLookup({}, { enabled: open });
  const { data: volumeLookupData, isLoading: isVolumeLookupLoading } =
    useQueryGetVolumeLookup({}, { enabled: open });
  const { data: temperatureLookupData, isLoading: isTemperatureLookupLoading } =
    useQueryGetTemperatureLookup({}, { enabled: open });
  const { data: weightLookupData, isLoading: isWeightLookupLoading } =
    useQueryGetWeightLookup({}, { enabled: open });
  const { data: fuelLookupData, isLoading: isFuelLookupLoading } =
    useQueryGetFuelLookup({}, { enabled: open });
  const { data: positionLookupData, isLoading: isPositionLookupLoading } =
    useQueryGetPositionLookup({}, { enabled: open });
  const { data: clientSourceLookupData, isLoading: isClientSourceLookupLoading } =
    useQueryGetClientSourceLookup({}, { enabled: open });
  const {
    data: clientCompetitorLookupData,
    isLoading: isClientCompetitorLookupLoading,
  } = useQueryGetClientCompetitorLookup({}, { enabled: open });
  const {
    data: clientPositionTypeLookupData,
    isLoading: isClientPositionTypeLookupLoading,
  } = useQueryGetClientPositionTypeLookup({}, { enabled: open });
  const { data: paymentTypeLookupData, isLoading: isPaymentTypeLookupLoading } =
    useQueryGetPaymentTypeLookup({}, { enabled: open });
  const { data: paymentLookupData, isLoading: isPaymentLookupLoading } =
    useQueryGetPaymentLookup({}, { enabled: open });
  const {
    data: paymentSubsUnitLookupData,
    isLoading: isPaymentSubsUnitLookupLoading,
  } = useQueryGetPaymentSubsUnitLookup({}, { enabled: open });
  const {
    data: pprPaymentTermLookupData,
    isLoading: isPprPaymentTermLookupLoading,
  } = useQueryGetPprPaymentTermLookup({}, { enabled: open });
  const { data: productLookupData, isLoading: isProductLookupLoading } =
    useQueryGetProductLookup({}, { enabled: open });
  const customerDetail = customerDetailData?.data?.response;
  const vatOptions: CustomerSelectOption[] = [
    { label: t("active"), value: "true" },
    { label: t("inactive"), value: "false" },
  ];
  
  const selectLookups = {
    clientPresetOptions: mapLookupOptions(clientPresetLookupData),
    clientTypeOptions: mapLookupOptions(clientTypeLookupData),
    clientStatusOptions: translateClientStatusOptions(
      mapLookupOptions(clientStatusLookupData),
      t,
    ),
    languageOptions: mapLookupOptions(languageLookupData),
    countryOptions: mapLookupOptions(countryLookupData),
    customerCountryOptions: mapCountryCodeOptions(referenceCountriesData),
    currencyOptions: mapLookupOptions(currencyLookupData),
    distanceOptions: mapLookupOptions(distanceLookupData),
    volumeOptions: mapLookupOptions(volumeLookupData),
    temperatureOptions: mapLookupOptions(temperatureLookupData),
    weightOptions: mapLookupOptions(weightLookupData),
    fuelOptions: mapLookupOptions(fuelLookupData),
    positionOptions: mapLookupOptions(positionLookupData),
    productOptions: mapLookupOptions(productLookupData),
    clientSourceOptions: mapLookupOptions(clientSourceLookupData),
    clientCompetitorOptions: mapLookupOptions(clientCompetitorLookupData),
    clientPositionTypeOptions: mapLookupOptions(clientPositionTypeLookupData),
    vatOptions,
    paymentTypeOptions: mapLookupOptions(paymentTypeLookupData),
    paymentOptions: mapLookupOptions(paymentLookupData),
    paymentSubsUnitOptions: mapLookupOptions(paymentSubsUnitLookupData),
    pprPaymentTermOptions: mapLookupOptions(pprPaymentTermLookupData),
    clientPresetLoading: isClientPresetLookupLoading,
    clientTypeLoading: isClientTypeLookupLoading,
    clientStatusLoading: isClientStatusLookupLoading,
    languageLoading: isLanguageLookupLoading,
    countryLoading: isCountryLookupLoading,
    customerCountryLoading: isReferenceCountriesLoading,
    currencyLoading: isCurrencyLookupLoading,
    distanceLoading: isDistanceLookupLoading,
    volumeLoading: isVolumeLookupLoading,
    temperatureLoading: isTemperatureLookupLoading,
    weightLoading: isWeightLookupLoading,
    fuelLoading: isFuelLookupLoading,
    positionLoading: isPositionLookupLoading,
    productLoading: isProductLookupLoading,
    clientSourceLoading: isClientSourceLookupLoading,
    clientCompetitorLoading: isClientCompetitorLookupLoading,
    clientPositionTypeLoading: isClientPositionTypeLookupLoading,
    paymentTypeLoading: isPaymentTypeLookupLoading,
    paymentLoading: isPaymentLookupLoading,
    paymentSubsUnitLoading: isPaymentSubsUnitLookupLoading,
    pprPaymentTermLoading: isPprPaymentTermLookupLoading,
  };

  useEffect(() => {
    if (!open) return;
    setActiveKey("main_information");
    form.resetFields();
    initialValues.current = {};

    if (!editCustomerId) {
      return;
    }
  }, [editCustomerId, form, open]);

  useEffect(() => {
    if (!open || !editCustomerId || !customerDetail) return;
    const values = mapCustomerToFormValues(customerDetail);
    initialValues.current = values;
    form.setFieldsValue(values);
  }, [customerDetail, editCustomerId, form, open]);

  useEffect(() => {
    if (!open) return;

    const invalidFieldNames = form
      .getFieldsError()
      .filter(({ errors }) => errors.length > 0)
      .map(({ name }) => name);

    if (invalidFieldNames.length > 0) {
      void form.validateFields(invalidFieldNames).catch(() => undefined);
    }
  }, [form, i18n.resolvedLanguage, open]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleFinish = async () => {
    const values = form.getFieldsValue(true);
    const editDetail = pickDetailData(customerDetail);
     
    const payload = {
      "client": {
        ...(editCustomerId && { id: editDetail.client.id }),
        // "active": true,
        "distributorId": selectedDistributor,
        "companyName": values?.companyName,
        "clientTypeId": values?.clientType,
        "statusId": values?.state,
        "email": values?.email,
        "clientManagerId": values?.clientManager,
        "clientPresetId": values?.clientPreset,
        "comment": values?.comment,
        "languageId": values?.language,
        "country": values?.country,
        "legalName": values?.legalName,
        "businessTypeIds": values?.businessType,
        "regNo": values?.regNo,
        "address": values?.address,
        "billAddress": values?.billAddress,
        "handlingInPlace": values?.handlingInPlace,
        "vat": values?.vatNr,
        "bank": values?.bank,
        "accountNo": values?.accountNo,
        "swift": values?.swift,
        "sourceId": values?.source,
        "competitorsId": values?.competitor,
      },
      "contact": {
        ...(editCustomerId && { id: editDetail.contact.id }),
        // "active": true,
        "name": values?.name,
        "surname": values?.surname,
        "phoneNumber": values?.phoneNumber,
        "email": values?.contactEmail,
        "positionId": values?.positionType,
        "positionName": values?.positionName,
      },
      "invoiceSetting": {
        ...(editCustomerId && { id: editDetail.invoiceSetting.id }),
        "userVat": values?.invoiceVat === "true",
        "countryId": values?.invoiceCountry,
        "paymentTypeId": values?.paymentType,
        "paymentPeriodId": values?.paymentPeriod,
        "paySubscriptionId": values?.paySubscriptionUntil,
        "pprId": values?.pprPaymentTerm,
        // "vatNr": values?.invoiceVatNr,
        // "bank": values?.invoiceBank,
      },
      "userInterfaceDetails": {
        ...(editCustomerId && { id: editDetail.userInterfaceDetails.id }),
        "currencyId": values?.currencyId,
        "distanceId": values?.distanceId,
        "volumeId": values?.volumeId,
        "temperatureId": values?.temperatureId,
        "weightId": values?.weightId,
        "measurementOfFuelConsumptionId": values?.fuelConsumptionId,
        // "active": true,
      },
      // "productPricing": [
      //   {
      //     ...(editCustomerId && { id: editDetail.firstProductPricing.id }),
      //     // "productId": values?.product,
      //     "prices": values?.price
      //   }
      // ],
    }

    console.log("Create customer form values:", values);
    console.log("Create customer payload:", payload);

    if (editCustomerId) {
      const changes = buildCustomerUpdatePayload(
        values,
        initialValues.current,
      );
      if (Object.keys(changes).length === 0) {
        handleClose();
        return;
      }
      await updateCustomer({ id: editCustomerId, payload: changes });
      message.success(t("customer_updated_successfully"));
    } else {
      await createCustomerMutation(payload);
      message.success(t("customer_created_successfully"));
    }

    await refetchList();
    handleClose();
  };

  const handleFinishFailed = () => {
    const values = form.getFieldsValue(true);
    console.log("Create customer form values:", values);
  };

  return (
    <CustomModal
      title={t(editRecord?.id ? "edit_customer" : "new_customer")}
      open={open}
      onCancel={handleClose}
      width="80%"
      style={{ maxWidth: 881 }}
      className="customer-modal"
      footer={null}
      centered
    >
      <Form
        form={form}
        onFinish={handleFinish}
        onFinishFailed={handleFinishFailed}
        noValidate
        preserve
        className="flex min-h-[560px] flex-col justify-between"
      >
        {isCustomerDetailLoading ? (
          <Loader height="h-[530px]" />
        ) : (
          <>
            <CustomerTabs
              form={form}
              activeKey={activeKey}
              setActiveKey={setActiveKey}
              selectLookups={selectLookups}
            />
            <CustomActionButtons
              onCancel={handleClose}
              loading={isCreating || isUpdating}
              disabled={isCustomerDetailLoading}
              okProps={{ htmlType: "submit" }}
            />
          </>
        )}
      </Form>
    </CustomModal>
  );
};

export default CreateCustomer;
